import { useEffect, useState } from "react";
import { reviews, signal } from "../lib/data";

type Filter = "active" | "resolved" | "all";

export default function Signal() {
  const [filter, setFilter] = useState<Filter>("active");
  const [expanded, setExpanded] = useState<string | null>("sig-amazon-kol");
  const [triVisible, setTriVisible] = useState(false);
  const [evVisible, setEvVisible] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    const t1 = window.setTimeout(() => setTriVisible(true), 400);
    const t2 = window.setTimeout(() => setEvVisible(true), 900);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const showToast = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 2400);
  };

  return (
    <div className="stagger">
      <div className="signal-head">
        <h1 className="h-page">Signals</h1>
        <div className="sub">What Pulse thinks needs your attention. Triangulated across survey, qual, and social - with a clear recommendation on which signal to trust.</div>
      </div>

      <div className="signal-filter">
        <button className={"sf-tab " + (filter === "active" ? "active" : "")} onClick={() => setFilter("active")}>Active <span className="count">3</span></button>
        <button className={"sf-tab " + (filter === "resolved" ? "active" : "")} onClick={() => setFilter("resolved")}>Resolved <span className="count">12</span></button>
        <button className={"sf-tab " + (filter === "all" ? "active" : "")} onClick={() => setFilter("all")}>All <span className="count">15</span></button>
      </div>

      <div className="alerts">

        {/* HIGH PRIORITY - Amazon review manipulation */}
        <div className="alert-card hi">
          <div className="alert-icon">
            <svg viewBox="0 0 24 24"><path d="M12 3 L22 20 L2 20 Z"/><line x1="12" y1="10" x2="12" y2="14"/><line x1="12" y1="17" x2="12" y2="17.5"/></svg>
          </div>
          <div className="alert-body">
            <div className="title">Preethi · Amazon Kolkata — suspected review manipulation</div>
            <div className="desc">
              Rating jumped from <b className="num">{signal.social.before}</b> to <b className="num">{signal.social.score}</b> in {signal.social.window}. Review volume tripled. {signal.social.share5star}% of new reviews are 5-star with generic language; {signal.social.window48h}% were posted inside a 48-hour window. The quant survey and qual interviews do not support this spike.
            </div>
            <div className="stats">
              <div className="s"><div className="k">New reviews</div><div className="v">{signal.social.volume.toLocaleString()}</div></div>
              <div className="s"><div className="k">5-star share</div><div className="v warn">{signal.social.share5star}%</div></div>
              <div className="s"><div className="k">48h window</div><div className="v warn">{signal.social.window48h}%</div></div>
              <div className="s"><div className="k">Pulse read</div><div className="v">Social is off</div></div>
            </div>

            {triVisible && expanded === "sig-amazon-kol" && (
              <>
                <div className="tri-box" style={{ animation: "fadeUp 360ms cubic-bezier(.2,.7,.2,1)" }}>
                  <div className="c">
                    <div className="lab"><span className="dot" />Survey · Quant</div>
                    <div className="big">{signal.quant.score}<small> / 5</small></div>
                    <div className="note">Kolkata owners, Wave 2, n=512. Steady against Wave 1. No anomaly.</div>
                  </div>
                  <div className="c ql">
                    <div className="lab"><span className="dot" />Depth interviews · Qual</div>
                    <div className="big" style={{ fontSize: 18 }}>Mixed, leaning concerned</div>
                    <div className="note"><q>The motor is strong, but after six months the jar started to crack. Everyone in my building faced this.</q><br /><span className="num muted" style={{ fontSize: 11 }}>— Papiya, DI 3, Kolkata</span></div>
                  </div>
                  <div className="c sc flag">
                    <div className="lab"><span className="dot" />Amazon · Social</div>
                    <div className="big">{signal.social.score}<small> / 5 · spike</small></div>
                    <div className="note">Volume tripled in 6 days, language overlap 84%. Likely manipulation.</div>
                  </div>
                </div>

                {evVisible && (
                  <div className="evidence-strip" style={{ animation: "fadeUp 380ms cubic-bezier(.2,.7,.2,1)" }}>
                    <div>
                      <h5>Pulse's recommendation</h5>
                      <div className="lede">The <em>social signal is off.</em> Trust the survey and the qual.</div>
                      <div className="subl">Do not use Amazon ratings for the Preethi Kolkata Q2 review cycle. Keep the survey as primary; let the qual soften the tone.</div>
                    </div>
                    <div>
                      <h5>Evidence — 6 of 3,118 flagged reviews</h5>
                      <div className="tiles">
                        {reviews.slice(0, 6).map((r, i) => (
                          <div key={i} className="tile">
                            <div className="stars">★★★★★</div>
                            {r}
                            <div className="ts">2026-03-21 {String(11 + (i % 6)).padStart(2, "0")}:{String((i * 13) % 60).padStart(2, "0")}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="alert-actions">
            <div className="ts">Flagged 5h ago</div>
            <div className="row">
              <button className="btn-ghost" onClick={() => showToast("Alert dismissed. Pulse will keep watching.")}>Dismiss</button>
              <button className="btn-secondary" onClick={() => setExpanded(expanded === "sig-amazon-kol" ? null : "sig-amazon-kol")}>
                {expanded === "sig-amazon-kol" ? "Collapse" : "Inspect"}
              </button>
              <button className="btn-primary" onClick={() => showToast("Investigation queued. Pulse will deep-dive.")}>Investigate</button>
            </div>
          </div>
        </div>

        {/* MEDIUM - drift */}
        <div className="alert-card md">
          <div className="alert-icon md">
            <svg viewBox="0 0 24 24"><path d="M3 17v4a1 1 0 0 0 1 1h4"/><path d="M21 7V3a1 1 0 0 0-1-1h-4"/><line x1="3" y1="22" x2="21" y2="4"/></svg>
          </div>
          <div className="alert-body">
            <div className="title">Category drift · "Premium" attribute in Mixer Grinder</div>
            <div className="desc">
              Across all six brands tracked, "premium" scores in Wave 2 are 1.1 points higher than Wave 1 - but absolute spend did not grow. Consumers are rating the category as premium without paying for premium. This is a category perception shift worth noting before the next wave designs its premium module.
            </div>
            <div className="stats">
              <div className="s"><div className="k">Brands affected</div><div className="v">6 of 6</div></div>
              <div className="s"><div className="k">Wave-on-wave</div><div className="v">+1.1 pts</div></div>
              <div className="s"><div className="k">Spend growth</div><div className="v">+0.3%</div></div>
              <div className="s"><div className="k">Pulse read</div><div className="v">Perception drift</div></div>
            </div>
          </div>
          <div className="alert-actions">
            <div className="ts">Flagged yesterday</div>
            <div className="row">
              <button className="btn-ghost">Dismiss</button>
              <button className="btn-secondary">Inspect</button>
            </div>
          </div>
        </div>

        {/* LOW - informational */}
        <div className="alert-card lo">
          <div className="alert-icon lo">
            <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12" y2="16.5"/></svg>
          </div>
          <div className="alert-body">
            <div className="title">Wave 3 fieldwork begins in 14 days</div>
            <div className="desc">Fieldwork plan is drafted. 12 cities, n=1,800 target, new "after-sales experience" module added. Pulse will auto-flag any wave-on-wave drifts in brand codes, question labels, or category definitions.</div>
          </div>
          <div className="alert-actions">
            <div className="ts">This morning</div>
            <div className="row">
              <button className="btn-ghost">Snooze</button>
              <button className="btn-secondary">Review plan</button>
            </div>
          </div>
        </div>

      </div>

      {toast && (
        <div className="toast" role="status">
          <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
          {toast}
        </div>
      )}
    </div>
  );
}
